import { InfiniteData, useQueryClient } from '@tanstack/react-query';

import amplitude from '@/amplitude';
import {
  AnalysisType as BackendAnalysisType,
  GetUserAnalysesPaginatedQuery,
  useCreateAnalysisMutation,
  useDeactivateAnalysisMutation,
  useRemoveDeactivatedAnalysisMutation,
  useResetAnalysisNewSellsCounterMutation,
  useUpdateAnalysisNameMutation,
} from '@/shared/hooks/queries/generated';
import type { AnalysisStatus, AnalysisType } from '@/shared/types/analysis';

/**
 * Constants
 */
const GetAnalysisByIdQueryId = 'GetAnalysisById';
const GetUserAnalysesPaginatedQueryId = 'GetUserAnalysesPaginated';
const GetUserAnalysesPaginatedQueryTypes = ['catalog', 'profile'] as const;

/**
 * Note sur les statuts :
 * - Backend : 'active' (en cours) / 'inactive' (terminé)
 * - Frontend : 'active' (en cours) / 'inactive' (terminé)
 * - Le mapping se fait automatiquement dans les mappers
 */

/**
 * Hook pour gérer le renommage d'analyse avec optimistic update
 */
export const useRenameAnalysis = () => {
  const queryClient = useQueryClient();
  const updateNameMutation = useUpdateAnalysisNameMutation();

  const renameAnalysis = async (analysisId: string, newName: string) => {
    const trimmedName = newName.trim();

    // Optimistic update pour GetAnalysisById
    queryClient.setQueryData([GetAnalysisByIdQueryId, { id: analysisId }], (oldData: any) => {
      if (oldData?.getAnalysisById) {
        return {
          ...oldData,
          getAnalysisById: {
            ...oldData.getAnalysisById,
            name: trimmedName,
          },
        };
      }
      return oldData;
    });

    // Optimistic update pour GetUserAnalysesPaginated
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>(
        {
          queryKey: [GetUserAnalysesPaginatedQueryId, queryType],
        },
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                getUserAnalysesPaginated: {
                  ...page.getUserAnalysesPaginated,
                  analyses: page.getUserAnalysesPaginated.analyses.map((analysis) =>
                    analysis.id === analysisId ? { ...analysis, name: trimmedName } : analysis,
                  ),
                },
              })),
            };
          }
          return oldData;
        },
      );
    }

    try {
      await updateNameMutation.mutateAsync({
        input: {
          id: analysisId,
          name: trimmedName,
        },
      });

      console.log('Analyse renommée avec succès');
    } catch (error) {
      // En cas d'erreur, restaurer les données originales
      queryClient.invalidateQueries({ queryKey: [GetAnalysisByIdQueryId, { id: analysisId }] });
      for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
        queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      }

      console.error('Erreur lors du renommage:', error);
      throw error;
    }
  };

  return {
    renameAnalysis,
    isRenaming: updateNameMutation.isPending,
  };
};

/**
 * Hook pour gérer l'arrêt d'analyse avec optimistic update
 */
export const useStopAnalysis = () => {
  const queryClient = useQueryClient();
  const deactivateAnalysisMutation = useDeactivateAnalysisMutation();

  const stopAnalysis = async (analysisId: string) => {
    // Optimistic update pour GetAnalysisById - changer le statut backend en 'inactive'
    queryClient.setQueryData([GetAnalysisByIdQueryId, { id: analysisId }], (oldData: any) => {
      if (oldData?.getAnalysisById) {
        return {
          ...oldData,
          getAnalysisById: {
            ...oldData.getAnalysisById,
            status: 'inactive', // Statut backend pour une analyse arrêtée
            endedAt: new Date(), // Mettre à jour la date de fin
          },
        };
      }
      return oldData;
    });

    // Optimistic update pour GetUserAnalysesPaginated
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>(
        {
          queryKey: [GetUserAnalysesPaginatedQueryId, queryType],
        },
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                getUserAnalysesPaginated: {
                  ...page.getUserAnalysesPaginated,
                  analyses: page.getUserAnalysesPaginated.analyses.map((analysis) =>
                    analysis.id === analysisId
                      ? {
                          ...analysis,
                          status: 'inactive', // Statut backend pour une analyse arrêtée
                          endedAt: new Date(), // Mettre à jour la date de fin
                        }
                      : analysis,
                  ),
                },
              })),
            };
          }
          return oldData;
        },
      );
    }

    try {
      await deactivateAnalysisMutation.mutateAsync({ id: analysisId });

      console.log('Analyse arrêtée avec succès');
    } catch (error) {
      // En cas d'erreur, restaurer les données originales
      queryClient.invalidateQueries({ queryKey: [GetAnalysisByIdQueryId, { id: analysisId }] });
      for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
        queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      }

      console.error("Erreur lors de l'arrêt:", error);
      throw error;
    }
  };

  return {
    stopAnalysis,
    isStopping: deactivateAnalysisMutation.isPending,
  };
};

/**
 * Hook pour gérer la suppression d'analyse avec optimistic update
 */
export const useDeleteAnalysis = () => {
  const queryClient = useQueryClient();
  const deactivateAnalysisMutation = useDeactivateAnalysisMutation();
  const removeAnalysisMutation = useRemoveDeactivatedAnalysisMutation();

  const deleteAnalysis = async (analysisId: string, currentStatus?: AnalysisStatus) => {
    // Optimistic update pour GetUserAnalysesPaginated
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>(
        {
          queryKey: [GetUserAnalysesPaginatedQueryId, queryType],
        },
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                getUserAnalysesPaginated: {
                  ...page.getUserAnalysesPaginated,
                  analyses: page.getUserAnalysesPaginated.analyses.filter((analysis) => analysis.id !== analysisId),
                },
              })),
            };
          }
          return oldData;
        },
      );
    }

    try {
      // Désactiver si nécessaire, puis supprimer
      if (currentStatus === 'active') {
        await deactivateAnalysisMutation.mutateAsync({ id: analysisId });
      }
      await removeAnalysisMutation.mutateAsync({ id: analysisId });

      console.log('Analyse supprimée avec succès');
    } catch (error) {
      // En cas d'erreur, restaurer les données originales
      for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
        queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      }

      console.error('Erreur lors de la suppression:', error);
      throw error;
    }
  };

  return {
    deleteAnalysis,
    isDeleting: deactivateAnalysisMutation.isPending || removeAnalysisMutation.isPending,
  };
};

interface CreateAnalysisData {
  title: string;
  url: string;
  type: AnalysisType;
}

/**
 * Hook pour gérer la création d'analyse avec optimistic update
 */
export const useCreateAnalysis = () => {
  const queryClient = useQueryClient();
  const createAnalysisMutation = useCreateAnalysisMutation();

  const createAnalysis = async (data: CreateAnalysisData) => {
    let analysisType: BackendAnalysisType;
    let queryType: (typeof GetUserAnalysesPaginatedQueryTypes)[number];
    switch (data.type) {
      case 'product':
        analysisType = 'catalog';
        queryType = 'catalog';
        break;
      case 'vendor':
        analysisType = 'profile';
        queryType = 'profile';
        break;
      default:
        throw new Error(`Type d'analyse non supporté: ${data.type}`);
    }

    try {
      const result = await createAnalysisMutation.mutateAsync({
        input: {
          name: data.title,
          pageUrl: data.url,
          type: analysisType,
        },
      });

      // // Optimistic update : ajouter l'analyse créée dans la liste GetUserAnalyses
      // queryClient.setQueryData(['GetUserAnalyses'], (oldData: any) => {
      //   if (oldData?.getUserAnalyses) {
      //     return {
      //       ...oldData,
      //       getUserAnalyses: [result.createAnalysis, ...oldData.getUserAnalyses],
      //     };
      //   }
      //   return oldData;
      // });

      // Envoi d'un événement à Amplitude
      if (data.type === 'product') {
        amplitude.track('Produits Add Completed');
      } else if (data.type === 'vendor') {
        amplitude.track('Vendeurs Add Completed');
      }

      return {
        success: true,
        analysis: result.createAnalysis,
      };
    } catch (error) {
      console.error("Erreur lors de la création de l'analyse:", error);

      // Envoyer un événement d'erreur à Amplitude
      if (data.type === 'product') {
        amplitude.track('Produits Add Failed', { error: (error as Error).message });
      } else if (data.type === 'vendor') {
        amplitude.track('Vendeurs Add Failed', { error: (error as Error).message });
      }

      return {
        success: false,
        error: "Erreur lors de la création de l'analyse",
      };
    } finally {
      // Invalider les queries pour rafraîchir les données
      await queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
    }
  };

  return {
    createAnalysis,
    isCreating: createAnalysisMutation.isPending,
    error: createAnalysisMutation.error ? "Erreur lors de la création de l'analyse" : null,
  };
};

/**
 * Hook pour gérer le reset du compteur de nouvelles ventes avec optimistic update
 */
export const useResetAnalysisNewSellsCounter = () => {
  const queryClient = useQueryClient();
  const resetCounterMutation = useResetAnalysisNewSellsCounterMutation();

  const resetAnalysisNewSellsCounter = async (analysisId: string) => {
    // Optimistic update pour GetAnalysisById
    queryClient.setQueryData([GetAnalysisByIdQueryId, { id: analysisId }], (oldData: any) => {
      if (oldData?.getAnalysisById) {
        return {
          ...oldData,
          getAnalysisById: {
            ...oldData.getAnalysisById,
            newSells: 0,
          },
        };
      }
      return oldData;
    });

    // Optimistic update pour GetUserAnalysesPaginated
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>(
        {
          queryKey: [GetUserAnalysesPaginatedQueryId, queryType],
        },
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                getUserAnalysesPaginated: {
                  ...page.getUserAnalysesPaginated,
                  analyses: page.getUserAnalysesPaginated.analyses.map((analysis) =>
                    analysis.id === analysisId ? { ...analysis, newSells: 0 } : analysis,
                  ),
                },
              })),
            };
          }
          return oldData;
        },
      );
    }

    try {
      await resetCounterMutation.mutateAsync({
        analysisId,
      });

      console.log('Compteur de nouvelles ventes remis à zéro avec succès');
    } catch (error) {
      // En cas d'erreur, restaurer les données originales
      queryClient.invalidateQueries({ queryKey: [GetAnalysisByIdQueryId, { id: analysisId }] });
      for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
        queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      }

      console.error('Erreur lors de la réinitialisation du compteur:', error);
      throw error;
    }
  };

  return {
    resetAnalysisNewSellsCounter,
    isResetting: resetCounterMutation.isPending,
  };
};
